import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useNavbarSubtitulo } from '../../context/NavbarSubtituloContext';

// ── Tipos ────────────────────────────────────────────────────────────────────
type EstadoToma = 'abierta' | 'contada' | 'aplicada' | 'anulada';

interface Toma {
  id: number;
  bodega_id: number;
  estado: EstadoToma;
  descripcion: string | null;
  creada_at: string;
  contada_at: string | null;
}

interface LineaToma {
  id: number;
  inv_producto_id: number;
  codigo: string | null;
  descripcion: string | null;
  unidad: string | null;
  stock_sistema: number;
  cantidad_fisica: number | null;
  contada: boolean;
  observacion: string | null;
}

interface Bodega { id: number; nombre: string; es_principal: boolean }
interface Categoria { id: number; nombre: string }

const ESTADO_LABEL: Record<EstadoToma, string> = {
  abierta: 'En conteo', contada: 'Terminada', aplicada: 'Aplicada', anulada: 'Anulada',
};
const ESTADO_TONE: Record<EstadoToma, string> = {
  abierta: 'var(--tone-info-text)', contada: 'var(--tone-warn-text)',
  aplicada: 'var(--tone-success-text)', anulada: 'var(--ink-faint)',
};

function fmtFecha(s?: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
export function TomaInventarioPage() {
  const empresaId = useEmpresaId();
  const [usuario, setUsuario] = useState<string>('');
  const [vista, setVista] = useState<'lista' | 'conteo'>('lista');
  const [bodegas, setBodegas] = useState<Bodega[]>([]);
  const [tomas, setTomas] = useState<Toma[]>([]);
  const [tomaSel, setTomaSel] = useState<Toma | null>(null);
  const [lineas, setLineas] = useState<LineaToma[]>([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);
  // Departamentos (categorías de inventario) y mapa producto→categoría para filtrar.
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [catPorProducto, setCatPorProducto] = useState<Map<number, number>>(new Map());
  const [filtroDepto, setFiltroDepto] = useState<number | ''>('');
  // Mostrar el saldo del sistema solo a SU / SU-GE / Administrador (control interno).
  const [puedeVerSaldo, setPuedeVerSaldo] = useState(false);
  // Modal de confirmación (en vez de window.confirm — regla MYA).
  const [confirma, setConfirma] = useState<{ titulo: string; mensaje: string; textoOk: string; tono: 'warn' | 'accent'; onOk: () => void } | null>(null);

  useNavbarSubtitulo(vista === 'conteo' ? 'Conteo' : '', vista !== 'lista',
    useCallback(() => { setVista('lista'); setTomaSel(null); }, []));

  function showMsg(tipo: 'ok' | 'err', texto: string) {
    setMsg({ tipo, texto });
    window.setTimeout(() => setMsg(null), tipo === 'err' ? 8000 : 4000);
  }

  // Usuario actual (para creada_por) + privilegio para ver el saldo del sistema.
  // SU/SU-GE (emails fijos) o rol con perm_administrar ven el saldo; el bodeguero
  // normal cuenta a ciegas (control interno).
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      const u = data.user;
      setUsuario(u?.id || '');
      if (!u?.id) return;
      const SUPER_USER_EMAILS = new Set(['sistemasmya@hotmail.com', 'marcomv30@gmail.com']);
      if (SUPER_USER_EMAILS.has(String(u.email || '').toLowerCase())) {
        setPuedeVerSaldo(true);
        return;
      }
      const { data: rolData } = await supabase
        .from('app_usuario_roles')
        .select('rol:app_roles!inner(perm_administrar)')
        .eq('user_id', u.id)
        .eq('empresa_id', empresaId)
        .eq('activo', true);
      setPuedeVerSaldo(Array.isArray(rolData) && rolData.some((r: any) => r?.rol?.perm_administrar === true));
    });
  }, [empresaId]);

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data: bodegaFinca } = await supabase
      .from('inv_bodegas')
      .select('id, nombre, es_principal')
      .eq('empresa_id', empresaId)
      .eq('activo', true)
      .ilike('nombre', 'Finca')
      .maybeSingle();
    const { data: tms } = bodegaFinca
      ? await supabase
        .from('inv_tomas')
        .select('id, bodega_id, estado, descripcion, creada_at, contada_at')
        .eq('empresa_id', empresaId)
        .eq('bodega_id', bodegaFinca.id)
        .neq('estado', 'anulada')
        .order('creada_at', { ascending: false })
      : { data: [] as Toma[] };
    setBodegas(bodegaFinca ? [bodegaFinca as Bodega] : []);
    setTomas((tms ?? []) as Toma[]);
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Abrir una toma nueva. No se permite si ya hay una abierta para esa bodega:
  // se debe terminar (o anular) la existente antes. Evita tomas duplicadas.
  async function nuevaToma(bodega: Bodega) {
    if (String(bodega.nombre || '').trim().toLocaleLowerCase('es') !== 'finca') {
      showMsg('err', 'Aplicaciones solo permite realizar tomas físicas de Bodega Finca.');
      return;
    }
    setGuardando(true);
    try {
      const descripcion = `Toma ${bodega.nombre} ${new Date().toLocaleDateString('es-CR')}`;
      const { data, error } = await supabase.rpc('iniciar_toma_inventario_bloqueada', {
        p_empresa_id: empresaId,
        p_bodega_id: bodega.id,
        p_descripcion: descripcion,
      });
      const res = data as any;
      if (error || !res?.ok) {
        showMsg('err', error?.message || res?.error || 'No se pudo abrir la toma estricta.');
        return;
      }
      const { data: toma, error: tomaError } = await supabase
        .from('inv_tomas')
        .select('id, bodega_id, estado, descripcion, creada_at, contada_at')
        .eq('id', res.toma_id)
        .single();
      if (tomaError || !toma) {
        showMsg('err', tomaError?.message || 'La toma se abrió, pero no fue posible mostrarla.');
        await cargar();
        return;
      }
      showMsg('ok', `Toma estricta abierta con ${res.lineas} productos. Bodega Finca quedó bloqueada.`);
      await cargar();
      abrirConteo(toma as Toma);
    } finally {
      setGuardando(false);
    }
  }

  /*
  async function nuevaTomaAnteriorNoUsar(bodega: Bodega) {
    if (String(bodega.nombre || '').trim().toLocaleLowerCase('es') !== 'finca') {
      showMsg('err', 'Aplicaciones solo permite realizar tomas físicas de Bodega Finca.');
      return;
    }
    setGuardando(true);
    try {
      // Validar (en BD, no solo en memoria) que no exista una toma abierta de esta bodega.
      const { data: existentes } = await supabase
        .from('inv_tomas')
        .select('id')
        .eq('empresa_id', empresaId)
        .eq('bodega_id', bodega.id)
        .eq('estado', 'abierta')
        .limit(1);
      if (existentes && existentes.length > 0) {
        showMsg('err', `Ya hay una toma abierta de Bodega ${bodega.nombre}. Termínela o anúlela antes de abrir otra.`);
        await cargar();
        return;
      }

      const { data: toma, error: eT } = await supabase
        .from('inv_tomas')
        .insert({
          empresa_id: empresaId, bodega_id: bodega.id, estado: 'abierta',
          descripcion: `Toma ${bodega.nombre} ${new Date().toLocaleDateString('es-CR')}`,
          creada_por: usuario || null,
        })
        .select('*').single();
      if (eT || !toma) { showMsg('err', eT?.message || 'No se pudo crear la toma.'); return; }

      // Snapshot de los productos de ESTA bodega (inv_productos.bodega_id).
      // El ERP asigna bodega por categoría: EMP→Planta, resto→Finca.
      const { data: prods } = await supabase
        .from('inv_productos')
        .select('id, codigo, descripcion, unidad_medida, stock_actual')
        .eq('empresa_id', empresaId)
        .eq('bodega_id', bodega.id)
        .order('descripcion');
      const filas = (prods ?? []).map((p: any) => ({
        toma_id: toma.id, empresa_id: empresaId, inv_producto_id: p.id,
        codigo: p.codigo, descripcion: p.descripcion, unidad: p.unidad_medida,
        stock_sistema: Number(p.stock_actual || 0),
      }));
      // insertar en lotes
      for (let i = 0; i < filas.length; i += 200) {
        await supabase.from('inv_toma_lineas').insert(filas.slice(i, i + 200));
      }
      showMsg('ok', `Toma abierta con ${filas.length} productos.`);
      await cargar();
      abrirConteo(toma as Toma);
    } finally {
      setGuardando(false);
    }
  }

  */
  // Anular una toma abierta (descartarla). Libera la bodega para abrir otra.
  function anularToma(t: Toma) {
    const bod = bodegas.find(b => b.id === t.bodega_id);
    setConfirma({
      titulo: 'Anular toma y liberar bodega',
      mensaje: `¿Anular la toma de Bodega ${bod?.nombre || ''}? El conteo quedará en el historial y se liberarán los movimientos de la bodega.`,
      textoOk: 'Anular y liberar', tono: 'warn',
      onOk: async () => {
        setConfirma(null);
        setGuardando(true);
        try {
          const { data, error } = await supabase.rpc('anular_toma_inventario_bloqueada', {
            p_empresa_id: empresaId,
            p_toma_id: t.id,
          });
          const res = data as any;
          if (error || !res?.ok) {
            showMsg('err', error?.message || res?.error || 'No se pudo anular la toma.');
            return;
          }
          showMsg('ok', 'Toma anulada y Bodega Finca liberada.');
          await cargar();
        } finally {
          setGuardando(false);
        }
      },
    });
  }

  /*
  function anularTomaAnteriorNoUsar(t: Toma) {
    const bod = bodegas.find(b => b.id === t.bodega_id);
    setConfirma({
      titulo: 'Anular toma',
      mensaje: `¿Anular la toma de Bodega ${bod?.nombre || ''}? Se descartará el conteo.`,
      textoOk: 'Anular toma', tono: 'warn',
      onOk: async () => {
        setConfirma(null);
        setGuardando(true);
        try {
          await supabase.from('inv_toma_lineas').delete().eq('toma_id', t.id);
          const { error } = await supabase
            .from('inv_tomas')
            .update({ estado: 'anulada', updated_at: new Date().toISOString() })
            .eq('id', t.id);
          if (error) { showMsg('err', error.message); return; }
          showMsg('ok', 'Toma anulada.');
          await cargar();
        } finally {
          setGuardando(false);
        }
      },
    });
  }

  */
  async function abrirConteo(t: Toma) {
    setTomaSel(t);
    setVista('conteo');
    setLoading(true);
    setFiltroDepto('');
    const [{ data }, { data: cats }, { data: prods }] = await Promise.all([
      supabase
        .from('inv_toma_lineas')
        .select('id, inv_producto_id, codigo, descripcion, unidad, stock_sistema, cantidad_fisica, contada, observacion')
        .eq('toma_id', t.id)
        .order('descripcion'),
      supabase.from('inv_categorias').select('id, nombre').eq('empresa_id', empresaId).order('nombre'),
      supabase.from('inv_productos').select('id, categoria_id').eq('empresa_id', empresaId),
    ]);
    setLineas((data ?? []) as LineaToma[]);
    setCategorias((cats ?? []) as Categoria[]);
    setCatPorProducto(new Map((prods ?? []).map((p: any) => [p.id, p.categoria_id])));
    setLoading(false);
  }

  // Guardar la cantidad física de una línea (al perder foco / Enter).
  async function guardarLinea(linea: LineaToma, valor: string) {
    const cant = valor.trim() === '' ? null : Number(valor);
    if (valor.trim() !== '' && !Number.isFinite(cant!)) { showMsg('err', 'Cantidad inválida.'); return; }
    // optimista
    setLineas(prev => prev.map(l => l.id === linea.id
      ? { ...l, cantidad_fisica: cant, contada: cant !== null }
      : l));
    const { error } = await supabase
      .from('inv_toma_lineas')
      .update({
        cantidad_fisica: cant,
        contada: cant !== null,
        contada_por: cant !== null ? (usuario || null) : null,
        contada_at: cant !== null ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', linea.id);
    if (error) showMsg('err', 'No se guardó: ' + error.message);
  }

  // Marcar la toma como terminada (lista para que admin la aplique).
  async function ejecutarTerminar() {
    if (!tomaSel) return;
    setGuardando(true);
    try {
      const { data, error } = await supabase.rpc('finalizar_toma_inventario_bloqueada', {
        p_empresa_id: empresaId,
        p_toma_id: tomaSel.id,
      });
      const res = data as any;
      if (error || !res?.ok) {
        showMsg('err', error?.message || res?.error || 'No se pudo terminar la toma.');
        return;
      }
      showMsg('ok', 'Toma terminada. Bodega Finca continúa bloqueada hasta que Administración aplique o anule.');
      await cargar();
      setVista('lista');
      setTomaSel(null);
    } finally {
      setGuardando(false);
    }
  }

  /*
  async function ejecutarTerminarAnteriorNoUsar() {
    if (!tomaSel) return;
    setGuardando(true);
    try {
      const { error } = await supabase
        .from('inv_tomas')
        .update({ estado: 'contada', contada_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', tomaSel.id);
      if (error) { showMsg('err', error.message); return; }
      showMsg('ok', 'Toma marcada como terminada. Administración aplicará el ajuste.');
      await cargar();
      setVista('lista'); setTomaSel(null);
    } finally {
      setGuardando(false);
    }
  }

  */
  function terminarToma() {
    if (!tomaSel) return;
    const faltan = lineas.filter(l => !l.contada).length;
    if (faltan > 0) {
      setSoloPendientes(true);
      showMsg('err', `No se puede terminar: faltan ${faltan} producto(s) por contar. El inventario inicial exige un conteo completo.`);
      return;
    }
    ejecutarTerminar();
  }

  const lineasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return lineas.filter(l => {
      if (filtroDepto !== '' && catPorProducto.get(l.inv_producto_id) !== filtroDepto) return false;
      if (soloPendientes && l.contada) return false;
      if (!q) return true;
      return `${l.codigo} ${l.descripcion}`.toLowerCase().includes(q);
    });
  }, [lineas, busqueda, soloPendientes, filtroDepto, catPorProducto]);

  // Departamentos que tienen productos en esta toma (con su conteo), ordenados.
  const deptosDisponibles = useMemo(() => {
    const conteo = new Map<number, number>();
    lineas.forEach(l => {
      const cid = catPorProducto.get(l.inv_producto_id);
      if (cid != null) conteo.set(cid, (conteo.get(cid) ?? 0) + 1);
    });
    return categorias
      .filter(c => conteo.has(c.id))
      .map(c => ({ id: c.id, nombre: c.nombre, n: conteo.get(c.id)! }))
      .sort((a, b) => b.n - a.n);
  }, [lineas, categorias, catPorProducto]);

  const progreso = useMemo(() => {
    // Si hay departamento filtrado, el progreso es de ese depto; si no, del total.
    const base = filtroDepto === '' ? lineas : lineas.filter(l => catPorProducto.get(l.inv_producto_id) === filtroDepto);
    const total = base.length;
    const contadas = base.filter(l => l.contada).length;
    return { total, contadas, pct: total ? Math.round((contadas / total) * 100) : 0 };
  }, [lineas, filtroDepto, catPorProducto]);

  function renderMsg() {
    if (!msg) return null;
    return createPortal(
      <div style={{
        position: 'fixed', top: 16, right: 16, zIndex: 10000, maxWidth: 'min(90vw,420px)',
        padding: '12px 16px', borderRadius: 10, fontWeight: 700, fontSize: 13,
        border: '1px solid', boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
        background: msg.tipo === 'ok' ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
        borderColor: msg.tipo === 'ok' ? 'var(--tone-success-border)' : 'var(--tone-warn-border)',
        color: msg.tipo === 'ok' ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
      }}>{msg.texto}</div>,
      document.body,
    );
  }

  // Modal de confirmación (createPortal a document.body — regla MYA, sin window.confirm).
  function renderConfirma() {
    if (!confirma) return null;
    const tonoBg = confirma.tono === 'warn' ? 'var(--tone-warn-bg)' : 'var(--emp-accent-bg)';
    const tonoBorder = confirma.tono === 'warn' ? 'var(--tone-warn-border)' : 'var(--emp-accent)';
    const tonoTxt = confirma.tono === 'warn' ? 'var(--tone-warn-text)' : 'var(--emp-accent-txt)';
    return createPortal(
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        onMouseDown={() => setConfirma(null)}
      >
        <div
          style={{ width: '100%', maxWidth: 420, borderRadius: 16, border: '1px solid var(--line)', background: 'var(--surface-raised)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'hidden' }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{confirma.titulo}</div>
          </div>
          <div style={{ padding: '16px 18px', fontSize: 14, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
            {confirma.mensaje}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--line)' }}>
            <button onClick={() => setConfirma(null)} style={{
              padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              border: '1px solid var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)',
            }}>Cancelar</button>
            <button onClick={confirma.onOk} style={{
              padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer',
              border: `1px solid ${tonoBorder}`, background: tonoBg, color: tonoTxt,
            }}>{confirma.textoOk}</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // ── Vista LISTA de tomas ──────────────────────────────────────────────────
  if (vista === 'lista') {
    return (
      <div style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
        {renderMsg()}
        {renderConfirma()}
        <div style={{ padding: 16 }}>
          {/* Abrir nueva toma por bodega */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>
              Nueva toma física
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {bodegas.map(b => (
                <button key={b.id} onClick={() => nuevaToma(b)} disabled={guardando} style={{
                  padding: '12px 18px', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  border: '1px solid var(--emp-accent)', background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)',
                }}>📋 Bodega {b.nombre}</button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 12, color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>
            Tomas · {tomas.length}
          </div>
          {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: 40 }}>Cargando…</p>}
          {!loading && tomas.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--ink-muted)' }}>
              No hay tomas. Abra una nueva para empezar a contar.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tomas.map(t => {
              const bod = bodegas.find(b => b.id === t.bodega_id);
              const editable = t.estado === 'abierta';
              return (
                <div key={t.id} style={{
                  borderRadius: 14, border: '1px solid var(--line)', background: 'var(--surface-raised)',
                  opacity: editable ? 1 : 0.75, overflow: 'hidden',
                }}>
                  <button onClick={() => editable && abrirConteo(t)} disabled={!editable} style={{
                    width: '100%', textAlign: 'left', padding: 14, cursor: editable ? 'pointer' : 'default',
                    border: 'none', background: 'transparent',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Bodega {bod?.nombre || t.bodega_id}</span>
                      <span style={{
                        padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                        border: `1px solid ${ESTADO_TONE[t.estado]}`, color: ESTADO_TONE[t.estado], background: 'var(--surface-overlay)',
                      }}>{ESTADO_LABEL[t.estado]}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-muted)' }}>{t.descripcion}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-faint)', marginTop: 2 }}>Abierta: {fmtFecha(t.creada_at)}</div>
                    {editable && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--emp-accent-txt)', marginTop: 8, textAlign: 'center' }}>→ Tocar para continuar el conteo</div>}
                  </button>
                  {editable && (
                    <div style={{ borderTop: '1px solid var(--line)', padding: '8px 14px', textAlign: 'right' }}>
                      <button onClick={() => anularToma(t)} disabled={guardando} style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: guardando ? 'wait' : 'pointer',
                        border: '1px solid var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)',
                      }}>✕ Anular toma</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Vista CONTEO (levantar saldo físico) ──────────────────────────────────
  return (
    <div style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
      {renderMsg()}
      {renderConfirma()}
      <div style={{ padding: 16 }}>
        {/* Cabecera: progreso */}
        <div style={{ padding: 14, borderRadius: 14, marginBottom: 14, background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{tomaSel?.descripcion}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: progreso.contadas === progreso.total ? 'var(--tone-success-text)' : 'var(--ink-muted)' }}>
              {progreso.contadas}/{progreso.total} contados
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: 'var(--line-dim)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 999, background: 'linear-gradient(90deg,#16a34a,#22c55e)', width: `${progreso.pct}%`, transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* Buscador + filtro */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <input
              value={busqueda} onChange={e => setBusqueda(e.target.value)} inputMode="search"
              placeholder="Buscar producto…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 38px 10px 14px', borderRadius: 12, fontSize: 15, border: '1px solid var(--line)', background: 'var(--surface-raised)', color: 'var(--ink)', outline: 'none' }}
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda('')}
                aria-label="Limpiar búsqueda"
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent',
                  color: 'var(--ink-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1,
                }}
              >✕</button>
            )}
          </div>
          <button onClick={() => setSoloPendientes(p => !p)} style={{
            padding: '10px 14px', borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            border: `1px solid ${soloPendientes ? 'var(--emp-accent)' : 'var(--line)'}`,
            background: soloPendientes ? 'var(--emp-accent-bg)' : 'transparent',
            color: soloPendientes ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
          }}>Solo pendientes</button>
        </div>

        {/* Filtro por departamento (categoría de bodega) — chips horizontales */}
        {deptosDisponibles.length > 1 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' }}>
            {([{ id: '' as number | '', nombre: 'Todos', n: lineas.length }, ...deptosDisponibles]).map(d => {
              const activo = filtroDepto === d.id;
              return (
                <button key={String(d.id) || 'todos'} onClick={() => setFiltroDepto(d.id)} style={{
                  flexShrink: 0, minHeight: 38, padding: '6px 12px', borderRadius: 999,
                  cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: 7,
                  border: `1.5px solid ${activo ? 'var(--emp-accent)' : 'var(--line)'}`,
                  background: activo ? 'var(--emp-accent-bg)' : 'transparent',
                  color: activo ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                }}>
                  {d.nombre}
                  <span style={{ background: 'var(--surface-overlay)', color: 'var(--ink-muted)', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 800, minWidth: 20, textAlign: 'center' }}>{d.n}</span>
                </button>
              );
            })}
          </div>
        )}

        {loading && <p style={{ color: 'var(--ink-muted)', textAlign: 'center', padding: 40 }}>Cargando…</p>}

        {/* Lista de productos a contar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {lineasFiltradas.map(l => (
            <div key={l.id} style={{
              padding: 12, borderRadius: 12,
              border: `1px solid ${l.contada ? 'var(--tone-success-border)' : 'var(--line)'}`,
              background: l.contada ? 'var(--tone-success-bg)' : 'var(--surface-raised)',
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--accent)', marginRight: 8, fontWeight: 700 }}>{l.codigo}</span>
                  {l.descripcion}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{l.unidad || ''}</span>
                  {/* Saldo del sistema: solo SU/SU-GE/Administrador (control interno) */}
                  {puedeVerSaldo && (
                    <span style={{ color: 'var(--tone-info-text)', fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
                      Sistema: {l.stock_sistema.toLocaleString('es-CR', { maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--ink-faint)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Físico</div>
                <input
                  type="number" inputMode="decimal" step="any"
                  defaultValue={l.cantidad_fisica ?? ''}
                  onBlur={e => { if (e.target.value !== String(l.cantidad_fisica ?? '')) guardarLinea(l, e.target.value); }}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  placeholder="—"
                  style={{
                    width: 110, padding: '8px 10px', borderRadius: 8, fontSize: 16, fontWeight: 700, textAlign: 'right',
                    border: `1.5px solid ${l.contada ? 'var(--tone-success-border)' : 'var(--line)'}`,
                    background: 'var(--surface-overlay)', color: 'var(--ink)', outline: 'none',
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Acción: terminar toma */}
        <div style={{ marginTop: 16 }}>
          <button onClick={terminarToma} disabled={guardando} style={{
            width: '100%', padding: 16, borderRadius: 14, fontSize: 17, fontWeight: 800, cursor: guardando ? 'wait' : 'pointer',
            border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', color: '#fff', opacity: guardando ? 0.6 : 1,
          }}>{guardando ? 'Guardando…' : '✓ Terminar conteo'}</button>
          <div style={{ fontSize: 12, color: 'var(--ink-faint)', textAlign: 'center', marginTop: 8 }}>
            Al terminar, administración revisa y aplica el ajuste.
          </div>
        </div>
      </div>
    </div>
  );
}

export default TomaInventarioPage;
